import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { GetUserDetailsByCount } from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";

interface Lead {
  leadId: number;
  firstName: string;
  lastName: string | null;
  gender: string | null;
  dob: string | null;
  dom: string | null;
  mobile: string;
  countryCode: string;
  email: string;
  address: string | null;
  stateId: number | null;
  stateName: string | null;
  districtId: number | null;
  districtName: string | null;
  cityId: number | null;
  cityName: string | null;
  zipCode: string | null;
  sourceId: number;
  sourceName: string | null;
  subSourceId: number;
  subSourceName: string | null;
  currentStatus: number;
  currentStatusName: string | null;
  incomeFrom: number | null;
  incomeTo: number | null;
  budgetMin: number | null;
  budgetMax: number | null;
  initialAssignedUser: number;
  initialAssignedUserName: string | null;
  currentAssignedUser: number;
  currentAssignedUserName: string | null;
  reasonName?: string;
}

const LeadDetails: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Query parameters
  const [queryParams, setQueryParams] = useState({
    userId: "",
    tenantId: "",
    statusId: "",
    userName: "",
    statusName: "",
  });

  const isReasonVisible =
    queryParams.statusName?.toLowerCase().includes("lost") ||
    queryParams.statusName?.toLowerCase().includes("cancel");

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const userDataUpdated = location.state?.userDataUpdated; // Add this

        const previousMenuID = localStorage.getItem("activeMenuID");
        console.log("MenuID from location state:", menuID);
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  // Parse query parameters from URL
  useEffect(() => {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    const urlParams = new URLSearchParams(queryString);
    console.log("URL Params:", urlParams.toString());
    setQueryParams({
      userId: urlParams.get("userId") || "",
      tenantId: urlParams.get("tenantId") || "",
      statusId: urlParams.get("statusId") || "",
      userName: urlParams.get("userName") || "",
      statusName: urlParams.get("statusName") || "",
    });
  }, []);

  // Fetch lead details
  const fetchLeadDetails = async () => {
    if (!queryParams.userId || !queryParams.tenantId || !queryParams.statusId) {
      return;
    }

    try {
      setLoading(true);
      const response = await GetUserDetailsByCount({
        userId: Number(queryParams.userId),
        tenantId: Number(queryParams.tenantId),
        countid: Number(queryParams.statusId),
      });
      const data = await response;
      console.log("Lead Details Data:", data);
      const mapped = (data || []).map((item: any) => ({
        ...item,
        reasonName: item.reasonName || "N/A", // ✅ ADD
      }));

      setLeads(mapped);
      toast.success("Lead details loaded successfully");
    } catch (error) {
      console.error("Error fetching lead details:", error);
      toast.error("Failed to load lead details");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const leadDashbordRedirection = async (leadId: number) => {
    try {
      navigate("/LeadDashboard", {
        state: { leadId },
      });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };

  useEffect(() => {
    if (queryParams.userId && queryParams.tenantId && queryParams.statusId) {
      fetchLeadDetails();
    }
    console.log("Query Params:", queryParams);
  }, [queryParams]);

  // Filter leads based on search
  const filteredLeads = leads.filter(
    (lead) =>
      lead.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
      lead.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      lead.mobile?.includes(searchText)
  );

  // Export to Excel
  const exportToExcel = () => {
    try {
      const headers = [
        "Lead ID",
        "Name",
        "Mobile",
        "Email",
        "Source",
        "Sub Source",
        "Status",
      ];

      if (isReasonVisible) headers.push("Reason"); // ✅ ADD

      const rows = filteredLeads.map((lead) => {
        const row = [
          lead.leadId,
          `${lead.firstName || ""} ${lead.lastName || ""}`.trim(),
          lead.mobile,
          lead.email,
          lead.sourceName || "N/A",
          lead.subSourceName || "N/A",
          lead.currentStatusName || "N/A",
        ];

        if (isReasonVisible) row.push(lead.reasonName || "N/A"); // ✅ ADD

        return row;
      });

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `lead-details-${queryParams.statusName}-${new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lead Details - ${queryParams.statusName}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 1cm;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          h1 {
            text-align: center;
            color: #333;
            margin-bottom: 10px;
          }
          .subtitle {
            text-align: center;
            color: #666;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 11px;
          }
          th {
            background-color: #a78590;
            color: white;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .meta-info {
            margin-bottom: 15px;
            font-size: 12px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <h1>Lead Details Report</h1>
        <div class="subtitle">Status: ${queryParams.statusName} | User: ${queryParams.userName
        }</div>
        <div class="meta-info">
          <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
          <strong>Total Records:</strong> ${filteredLeads.length}
        </div>
        <table>
          <thead>
            <tr>
              <th>Lead ID</th>
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Source</th>
              ${isReasonVisible ? "<th>Reason</th>" : ""}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredLeads
          .map(
            (lead) => `
              <tr>
                <td>${lead.leadId}</td>
                <td>${lead.firstName || ""} ${lead.lastName || ""}</td>
                <td>${lead.countryCode} ${lead.mobile}</td>
                <td>${lead.email}</td>
                <td>${lead.sourceName || "N/A"}</td>
                ${isReasonVisible ? `<td>${lead.reasonName || "N/A"}</td>` : ""}
                <td>${lead.currentStatusName || "N/A"}</td>
              </tr>
            `
          )
          .join("")}
          </tbody>
        </table>
      </body>
      </html>
    `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const columns = [
    {
      name: "Name",
      cell: (row: Lead) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.firstName?.charAt(0).toUpperCase() || "?"}
          </div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              leadDashbordRedirection(row.leadId);
            }}
            style={{
              textDecoration: "none",
              fontWeight: 500,
              color: "#33475b",
            }}
          >
            <span style={{ fontWeight: "500", color: "#33475b" }}>
              {`${row.firstName || ""} ${row.lastName || ""}`.trim() || "N/A"}
            </span>
          </a>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Mobile",
      selector: (row: Lead) => row.mobile,
      sortable: true,
      width: "130px",
    },
    {
      name: "Email",
      selector: (row: Lead) => row.email,
      sortable: true,
      width: "200px",
    },
    {
      name: "Source Name",
      selector: (row: Lead) => row.sourceName || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: "Sub Source Name",
      selector: (row: Lead) => row.subSourceName || "N/A",
      sortable: true,
      width: "160px",
    },
    ...(isReasonVisible
      ? [
        {
          name: "Reason",
          selector: (row: Lead) => row.reasonName || "N/A",
          sortable: true,
          width: "180px",
        },
      ]
      : []),
    {
      name: "Status",
      selector: (row: Lead) => row.currentStatusName || "N/A",
      width: "120px",
    },
  ];

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div>
                        <div className="card-title mb-0">
                          Lead Details - {queryParams.statusName}
                        </div>
                        <small style={{ color: "#666" }}>
                          User: {queryParams.userName} | Status Name:{" "}
                          {queryParams.statusName}
                        </small>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Search by Name, Email or Mobile"
                          className="p-2 datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-secondary btn-sm"
                          onClick={() => window.close()}
                          title="Close Window"
                        >
                          <i className="bi bi-x-circle"></i> Close
                        </button>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredLeads}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No lead data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Lead Details"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .datatableSearchInput {
          border: 1px solid #dee2e6;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .datatableSearchInput:focus {
          outline: none;
          border-color: #a78590;
          box-shadow: 0 0 0 0.2rem rgba(167, 133, 144, 0.25);
        }
      `}</style>
    </div>
  );
};

export default LeadDetails;
